import React from "react";
import HeaderWithSearch from "./HeaderWithSearch";
import "./HighQualityGames.css";

// Every game here is built from scratch and hosted on this site — no
// third-party embeds, no streaming, no accounts. Download once and it
// keeps working offline.
const NATIVE = [
  {
    id: "block-blast",
    emoji: "🧩",
    title: "Block Blast",
    file: "block_blast.html",
    tagline: "Drag glossy 3D blocks onto the grid and blast full lines.",
    description:
      "A fast, native-feeling puzzle game. The entire game engine is written in C++ and compiled to WebAssembly for ultra-low latency, while a hand-tuned canvas renderer gives the flat 2D board a crisp, extruded 3D look.",
    badges: ["C++ engine", "WebAssembly", "Ultra-low latency", "2.5D visuals", "Plays offline"],
    steps: [
      "Click “Play now” to launch it instantly in your browser.",
      "Or click “Download” to save one self-contained file.",
      "Open that file any time — it runs fully offline, no internet needed.",
    ],
  },
  {
    id: "bubble-blast",
    emoji: "🫧",
    title: "Bubble Blast",
    file: "bubble_shooter.html",
    tagline: "Aim, chain colors, and clear the field before it reaches you.",
    description:
      "A from-scratch bubble shooter built entirely in vanilla JavaScript and Canvas 2D — same glossy, extruded look and neon color palette as Block Blast, right down to the score banner, combo popups, and particle bursts. Aim your shot, match 3+ same-colored bubbles to pop them, and clear floating clusters for bonus points.",
    badges: ["Vanilla JS", "Canvas 2D", "Physics-based aiming", "Plays offline"],
    steps: [
      "Click “Play now” to launch it instantly in your browser.",
      "Or click “Download” to save one self-contained file.",
      "Move (or drag) to aim, click/tap to shoot — or use ←/→ and Space.",
    ],
  },
  {
    id: "bubble-burst",
    emoji: "💥",
    title: "Bubble Burst",
    file: "bubble_burst.html",
    tagline: "Click a cluster of matching bubbles and watch a whole crowd of them pop at once.",
    description:
      "Another from-scratch original in the same glossy, neon family as Block Blast and Bubble Blast. No shooting this time — just click any group of 2+ matching bubbles to pop the entire cluster in one satisfying burst. Bigger clusters score a lot more, popped columns collapse and slide together, and clearing the whole board earns a bonus.",
    badges: ["Vanilla JS", "Canvas 2D", "Click-to-pop clusters", "Plays offline"],
    steps: [
      "Click “Play now” to launch it instantly in your browser.",
      "Or click “Download” to save one self-contained file.",
      "Hover to preview a cluster's size, then click/tap it to pop the whole group.",
    ],
  },
];

function NativeCard({ game }) {
  return (
    <article className="hqg-card">
      <div className="hqg-card-art" aria-hidden="true">
        <span className="hqg-card-emoji" role="img" aria-label={game.title}>
          {game.emoji}
        </span>
        <span className="hqg-card-glow" />
      </div>

      <div className="hqg-card-body">
        <h2 className="hqg-card-title">{game.title}</h2>
        <p className="hqg-card-tagline">{game.tagline}</p>
        <p className="hqg-card-desc">{game.description}</p>

        <ul className="hqg-badges">
          {game.badges.map((b) => (
            <li key={b} className="hqg-badge">{b}</li>
          ))}
        </ul>

        <div className="hqg-actions">
          <a
            className="hqg-btn hqg-btn-primary"
            href={`/${game.file}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            ▶ Play now
          </a>
          <a className="hqg-btn hqg-btn-ghost" href={`/${game.file}`} download={game.file}>
            ⬇ Download (offline)
          </a>
        </div>

        <div className="hqg-how">
          <strong>How it works</strong>
          <ol>
            {game.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </div>
      </div>
    </article>
  );
}

export default function HighQualityGames() {
  return (
    <>
      <HeaderWithSearch />
      <main className="hqg-page">
        <header className="hqg-hero">
          <p className="hqg-eyebrow">🎮 High Quality Video Games</p>
          <h1 className="hqg-title">Native-grade games, right in your browser</h1>
          <p className="hqg-sub">
            {NATIVE.length} games, built entirely from scratch and hosted here — no installs,
            no accounts, no streaming. Play instantly or download a copy that works offline.
          </p>
        </header>

        <section className="hqg-section">
          <h2 className="hqg-section-title">Built here — plays offline</h2>
          <p className="hqg-section-sub">
            Written from scratch, engine and all. Download it once and it runs with no connection.
          </p>
          <div className="hqg-grid">
            {NATIVE.map((g) => (
              <NativeCard key={g.id} game={g} />
            ))}
          </div>
        </section>
      </main>
    </>
  );
}
